import { useMutation } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';

import { Button } from '@/components/primitives/Button';
import { useTranslation } from '@/hooks/useTranslation';
import { signupApi } from '@/modules/auth/services/signupApi';
import { responseErrors } from '@/modules/settings/utils/responseErrors';

import { AUTH_TITLE_CLASS, AuthLayout } from '../components/AuthLayout';

import type { ReactNode } from 'react';

/**
 * Confirms the address on a click, not on load, so mail scanners that open links cannot. Makes the
 * account but no session: the tab that started the signup claims that.
 */
export function SignupVerifyPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const verify = useMutation({
    mutationFn: () => signupApi.verify(token),
  });
  const confirmed = verify.data?.state === 'promoted';

  if (!token || verify.isError || (verify.data && !confirmed)) {
    return (
      <SignupMessage
        title={t('Invalid link')}
        action={
          <Button appearance="solid" kind="inverse" scale="m" width="full" asChild>
            <Link to="/signup">{t('Start over')}</Link>
          </Button>
        }
      >
        <p>
          {responseErrors(verify.error).token ||
            t('This confirmation link is invalid or has expired.')}
        </p>
      </SignupMessage>
    );
  }

  if (confirmed) {
    return (
      <SignupMessage
        title={t('Email confirmed')}
        action={
          <Button appearance="outline" kind="neutral" scale="m" width="full" asChild>
            <Link to="/login">{t('Sign in')}</Link>
          </Button>
        }
      >
        <p>{t('Go back to the tab where you signed up — you are being signed in there.')}</p>
        <p>{t('Closed that tab? Your account is ready — sign in with the password you chose.')}</p>
      </SignupMessage>
    );
  }

  return (
    <SignupMessage
      title={t('Confirm your email')}
      action={
        <Button
          appearance="solid"
          kind="inverse"
          scale="m"
          width="full"
          loading={verify.isPending}
          disabled={verify.isPending}
          onClick={() => verify.mutate()}
        >
          {t('Confirm my email')}
        </Button>
      }
    >
      <p>{t('One last step to finish setting up your account.')}</p>
    </SignupMessage>
  );
}

function SignupMessage({
  title,
  children,
  action,
}: {
  title: string;
  children: ReactNode;
  action: ReactNode;
}) {
  return (
    <AuthLayout>
      <div className="flex flex-col gap-xxl xl:w-[400px]">
        <div className="flex flex-col gap-sm">
          <h1 className={AUTH_TITLE_CLASS}>{title}</h1>
          <div className="flex flex-col gap-sm text-size-n1 leading-[22px] text-shades-coal xl:text-foreground">
            {children}
          </div>
        </div>
        {action}
      </div>
    </AuthLayout>
  );
}
